import { isThreeEM } from 'src/helpers/interface_helpers';
import type { Device } from 'src/types/Device';
import type { PeakPowerChannel, PeakPowerDeviceSnapshot } from './fetch_peak_power_statistics';

export type PeakPhaseChip = {
	channel: number;
	label: string;
	live: PeakPowerChannel | null;
	avg5: PeakPowerChannel | null;
	liveWatts: number;
	avg5Watts: number;
};

export type PeakDevicePanel = {
	deviceId: string;
	deviceName: string;
	device?: Device;
	isPreferred: boolean;
	isThreePhase: boolean;
	phases: PeakPhaseChip[];
};

export type PeakRankedRow = PeakPhaseChip & {
	id: string;
	deviceId: string;
	deviceName: string;
	device?: Device;
	rank: number;
	percent: number;
	differenceWatts: number;
};

export type PeakPowerOverviewModel = {
	hero: PeakRankedRow;
	rows: PeakRankedRow[];
	maxWatts: number;
};

export type PeakMeterRow = {
	rank: number;
	panel: PeakDevicePanel;
	strongest: PeakPhaseChip;
	percent: number;
};

export type PhaseSpread = {
	id: string;
	label: string;
	watts: number;
};

export type PhasePeakBalanceModel = {
	panel: PeakDevicePanel;
	phases: PeakPhaseChip[];
	strongest: PeakPhaseChip;
	weakest: PeakPhaseChip;
	spreadWatts: number;
	spreadPercent: number;
	isBalanced: boolean;
	pairSpreads: PhaseSpread[];
};

const parsePowerLabel = (label: string | undefined) => {
	if (!label) return 0;
	const match = label.replace(',', '.').match(/([\d.]+)\s*(kW|W|MW)?/i);
	if (!match) return 0;
	const amount = Number(match[1]);
	if (!Number.isFinite(amount)) return 0;
	const unit = (match[2] ?? 'W').toUpperCase();
	if (unit === 'KW') return amount * 1000;
	if (unit === 'MW') return amount * 1_000_000;
	return amount;
};

const channelWatts = (channel: PeakPowerChannel | null): number =>
	channel?.watts ?? parsePowerLabel(channel?.value);

const getPeakChannelIndices = (
	device: Device,
	liveRows: (PeakPowerChannel | null)[],
	avgRows: (PeakPowerChannel | null)[],
) => {
	if (isThreeEM(device?.type) && device?.mode !== 'monophase') {
		const count = Math.max(liveRows.length, avgRows.length);
		return Array.from({ length: count }, (_, channel) => channel);
	}

	return [device.channel ?? 0];
};

const buildPeakPhases = (
	device: Device,
	snapshot: PeakPowerDeviceSnapshot,
	phaseLabel: (device: Device, channel: number, channelCount: number) => string,
): PeakPhaseChip[] => {
	const liveRows = snapshot.live ?? [];
	const avgRows = snapshot.avg5 ?? [];
	const channelIndices = getPeakChannelIndices(device, liveRows, avgRows);
	const channelCount = channelIndices.length;

	return channelIndices
		.map((channel) => {
			const live = liveRows[channel] ?? null;
			const avg5 = avgRows[channel] ?? null;

			return {
				channel,
				label: phaseLabel(device, channel, channelCount),
				live,
				avg5,
				liveWatts: channelWatts(live),
				avg5Watts: channelWatts(avg5),
			};
		})
		.filter((phase) => phase.live || phase.avg5);
};

export const buildPeakDevicePanels = (
	snapshots: PeakPowerDeviceSnapshot[],
	options: {
		preferredDeviceId?: string | null;
		phaseLabel: (device: Device, channel: number, channelCount: number) => string;
	},
): PeakDevicePanel[] => {
	const preferredId =
		options.preferredDeviceId &&
		snapshots.some((snapshot) => snapshot.device.id === options.preferredDeviceId)
			? options.preferredDeviceId
			: null;

	const sorted = [...snapshots].sort((left, right) => {
		if (left.device.id === preferredId) return -1;
		if (right.device.id === preferredId) return 1;
		return (left.device.name ?? '').localeCompare(right.device.name ?? '', undefined, {
			sensitivity: 'base',
		});
	});

	return sorted
		.map((snapshot) => ({
			deviceId: snapshot.device.id,
			deviceName: snapshot.device.name,
			device: snapshot.device,
			isPreferred: snapshot.device.id === preferredId,
			isThreePhase: isThreeEM(snapshot.device?.type) && snapshot.device?.mode !== 'monophase',
			phases: buildPeakPhases(snapshot.device, snapshot, options.phaseLabel),
		}))
		.filter((panel) => panel.phases.length > 0);
};

export const buildPeakRankedRows = (panels: PeakDevicePanel[]): PeakRankedRow[] => {
	const sorted = panels
		.flatMap((panel) =>
			panel.phases.map((phase) => ({
				id: `${panel.deviceId}-${phase.channel}`,
				deviceId: panel.deviceId,
				deviceName: panel.deviceName,
				device: panel.device,
				...phase,
			})),
		)
		.sort((left, right) => right.liveWatts - left.liveWatts);
	const maxWatts = Math.max(sorted[0]?.liveWatts ?? 0, 1);

	return sorted.map((row, index) => ({
		...row,
		rank: index + 1,
		percent: (row.liveWatts / maxWatts) * 100,
		differenceWatts: row.liveWatts - row.avg5Watts,
	}));
};

export const buildPeakPowerOverviewModel = (
	panels: PeakDevicePanel[],
): PeakPowerOverviewModel | null => {
	const rows = buildPeakRankedRows(panels);
	if (!rows.length) return null;

	return {
		hero: rows[0],
		rows,
		maxWatts: Math.max(rows[0].liveWatts, 1),
	};
};

export const buildPeakMeterRows = (panels: PeakDevicePanel[]): PeakMeterRow[] => {
	const rows = panels
		.map((panel) => {
			const strongest = panel.phases.reduce(
				(current, phase) => (phase.liveWatts > current.liveWatts ? phase : current),
				panel.phases[0],
			);
			return strongest ? { panel, strongest } : null;
		})
		.filter((row): row is { panel: PeakDevicePanel; strongest: PeakPhaseChip } =>
			Boolean(row && (row.strongest.liveWatts > 0 || row.strongest.avg5Watts > 0)),
		) as Array<{
		panel: PeakDevicePanel;
		strongest: PeakPhaseChip;
	}>;

	rows.sort((left, right) => right.strongest.liveWatts - left.strongest.liveWatts);
	const maxWatts = Math.max(rows[0]?.strongest.liveWatts ?? 0, 1);

	return rows.slice(0, 5).map((row, index) => ({
		...row,
		rank: index + 1,
		percent: (row.strongest.liveWatts / maxWatts) * 100,
	}));
};

export const buildPhasePeakBalanceModel = (panels: PeakDevicePanel[]): PhasePeakBalanceModel | null => {
	const candidates = panels
		.filter((panel) => panel.isThreePhase && panel.phases.length >= 2)
		.map((panel) => ({
			panel,
			maxWatts: Math.max(...panel.phases.map((phase) => phase.liveWatts)),
		}))
		.sort((left, right) => right.maxWatts - left.maxWatts);
	const panel = candidates[0]?.panel;
	if (!panel) return null;

	const phases = [...panel.phases].sort((left, right) => right.liveWatts - left.liveWatts);
	const strongest = phases[0];
	const weakest = phases[phases.length - 1];
	const spreadWatts = Math.max(strongest.liveWatts - weakest.liveWatts, 0);
	const spreadPercent =
		strongest.liveWatts > 0 ? Math.round((spreadWatts / strongest.liveWatts) * 100) : 0;
	const pairSpreads: PhaseSpread[] = [];

	for (let leftIndex = 0; leftIndex < panel.phases.length; leftIndex += 1) {
		for (let rightIndex = leftIndex + 1; rightIndex < panel.phases.length; rightIndex += 1) {
			const left = panel.phases[leftIndex];
			const right = panel.phases[rightIndex];
			pairSpreads.push({
				id: `${left.channel}-${right.channel}`,
				label: `${left.label.replace('Phase ', '')}–${right.label.replace('Phase ', '')}`,
				watts: Math.abs(left.liveWatts - right.liveWatts),
			});
		}
	}

	return {
		panel,
		phases,
		strongest,
		weakest,
		spreadWatts,
		spreadPercent,
		isBalanced: spreadPercent <= 15,
		pairSpreads: pairSpreads.sort((left, right) => right.watts - left.watts),
	};
};
